/**
 * Pricing tiers + studio seat layout for the multi-spot booking checkout,
 * ported from the handoff `brands.jsx` (`class.pricing`) and `stepper-multi.jsx`
 * (`SC_SEAT_*`). Used until live product/spot data is wired in.
 */
export interface PricingTier {
  deferredMembershipPurchaseId?: string | undefined;
  isDeferredMembershipAccess?: boolean | undefined;
  deferredChargeSettings?:
    import("@zipper/embedded-api-client").DeferredChargeSettingsDto | undefined;
  id: string;
  label: string;
  sub: string;
  price: number;
  perSession: number | null;
  sessions: number | null;
  recurring: boolean;
  badge?: string;
  /** Real Stripe product id for this tier — populated from live data; empty in mock. */
  stripeProductId?: string;
  /**
   * The tier product's `prior`-trigger form, resolved from the org catalog
   * (`CatalogEntry.priorFormId`). Distinct from the class/appointment's own
   * forms: this is the one the CHARGE enforces, so a tier that has one cannot
   * be paid for until it is completed. Absent in mock data.
   */
  priorFormId?: number;
  /**
   * PLAT-6791: who may pick this tier in a multi-person booking. `buyer` =
   * only the signed-in member (their own free access or member price);
   * `others` = everyone except them (a membership they already own, still
   * purchasable for a guest); `owner` = only a buyer who owns the product (a
   * $0 access tier the shop can't price, so nobody else can buy it). Unset =
   * everyone.
   */
  audience?: 'buyer' | 'others' | 'owner';
  /**
   * PLAT-6805: a tier built from one family member's own membership, visible
   * only to the attendee with this `familyUserId`.
   */
  forFamilyUserId?: number;
  /** PLAT-6805: family attendees this tier is hidden from (they own it). */
  hideForFamilyUserIds?: number[];
}

export const MOCK_TIERS: PricingTier[] = [
  { id: 'dropin', label: 'Drop-in', sub: 'This class only', price: 38, perSession: 38, sessions: 1, recurring: false },
  { id: 'pack5', label: '5-class pack', sub: 'Save $20 · 90 days to use', price: 170, perSession: 34, sessions: 5, recurring: false, badge: 'Save $20' },
  { id: 'pack10', label: '10-class pack', sub: 'Save $60 · 90 days to use', price: 320, perSession: 32, sessions: 10, recurring: false, badge: 'Save $60' },
  { id: 'unlimited', label: 'Unlimited monthly', sub: 'All classes · cancel anytime', price: 219, perSession: null, sessions: null, recurring: true, badge: 'Most popular' },
];

export const isPack = (t: PricingTier) => !t.recurring && (t.sessions ?? 1) > 1;

export const SEAT_ROWS = ['A', 'B', 'C'];
export const SEAT_COLS = 6;
export const TAKEN_SEATS = new Set(['A3', 'B1', 'C5', 'C6']);
